import {useQueryClient} from '@tanstack/react-query'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import {Image} from 'primereact/image'
import {InputText} from 'primereact/inputtext'
import {InputTextarea} from 'primereact/inputtextarea'
import {useCallback, useEffect, useMemo, useRef} from 'react'
import {ReactComponent as FaMinusCircle} from '../../assets/icons/FaMinusCircle.svg'

import {useSignal, useSignals} from '@preact/signals-react/runtime'
import dischargeImg from '../../assets/images/DischargedAccount.svg'
import TranslateUtils from '../../i18n/TranslateUtils'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {useAuth} from '../authentication/AuthProvider'
import {component, getComposedDataList} from '../form/Constant'
import AuditLogButton from '../generic-log/AuditLogButton'
import GenericLogTable from '../generic-log/GenericLogTable'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {useDischargePatient} from '../patient/query-keys/PatientKeysManager'
import {permissionOptionKeys} from '../role/RoleConstant'
import {usePatientContext} from '../styled-component/information/StyleDetailInformationWrapper'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import {dischargeList} from './DischargeConstant'
import ExportDischargePdf from './ExportDischargePdf'
import {
    useDischargeByUser,
    useDischargeCRUD,
} from './query-keys/DischargeQueryManager'

const customLabelClassName = 'font-bold col-12 p-0'
const customFontSize = 'col-12'

const Discharge = ({userData = {}}) => {
    useSignals()
    //#region Context
    const {user} = useAuth()
    const {
        currentViewedUser,
        handleCheckPermissionByKey,
        handleChangeCurrentViewedUser,
    } = usePatientContext()
    //#endregion

    //#region Translate
    const {translateCommons, translateKeys} = TranslateUtils()
    //#endregion

    //#region State
    const disable = useSignal(true)
    const visible = useSignal(false)
    //#endregion

    //#region Effect
    useEffect(() => {
        if (!!currentViewedUser?.dischargedAt) {
            let timeout = 0
            // Convert the ISO string to a Date object in the user's local timezone
            let deletedDate = new Date(currentViewedUser?.dischargedAt)

            const timer = setInterval(() => {
                timeout = deletedDate - Date.now()

                if (timeout < 0) {
                    return
                }
                // Time calculations for days, hours, minutes and seconds
                const days = Math.floor(timeout / (1000 * 60 * 60 * 24))
                const hours = Math.floor(
                    (timeout % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
                )
                const minutes = Math.floor(
                    (timeout % (1000 * 60 * 60)) / (1000 * 60),
                )
                const seconds = Math.floor((timeout % (1000 * 60)) / 1000)

                const buttonCancel = document.getElementById('discharge-btn')
                if (buttonCancel) {
                    if (
                        days === 0 &&
                        hours === 0 &&
                        minutes === 0 &&
                        seconds === 0
                    ) {
                        buttonCancel.setAttribute('disable', true)
                    }

                    if (
                        (!!days || days === 0) &&
                        (!!hours || hours === 0) &&
                        (!!minutes || minutes === 0) &&
                        (!!seconds || seconds === 0)
                    ) {
                        if (
                            buttonCancel &&
                            buttonCancel?.childNodes?.length > 0
                        ) {
                            const buttonLabel = buttonCancel.childNodes[1]
                            if (buttonLabel) {
                                buttonLabel.innerHTML =
                                    translateCommons('CANCEL DISCHARGE') +
                                    ' ( ' +
                                    days +
                                    'd ' +
                                    hours +
                                    'h ' +
                                    minutes +
                                    'm ' +
                                    seconds +
                                    's ' +
                                    ' ) '
                            }
                        }
                    }
                }
            }, 1000)
            return () => clearInterval(timer)
        }
    }, [currentViewedUser.dischargedAt, translateCommons])
    //#endregion

    //#region Ref
    const genericRef = useRef(null)
    const exportRef = useRef(null)
    //#endregion

    //#region Query
    const queryClient = useQueryClient()
    const {dischargeKeys} = QueryKeyManager(QueryKeyTypes.discharge, user?.id)
    const {patientKeys} = QueryKeyManager(QueryKeyTypes.patient, user?.id)

    const patientQueryKeys = useMemo(() => {
        return patientKeys.detail({id: currentViewedUser?.id})
    }, [currentViewedUser?.id, patientKeys])

    const dischargeQueryKeys = useMemo(() => {
        return dischargeKeys.detailByUser({userId: currentViewedUser?.id})
    }, [dischargeKeys, currentViewedUser?.id])

    const {data} = useDischargeByUser(
        user?.id,
        currentViewedUser?.id,
        !!user?.id && !!currentViewedUser?.id,
    )

    const {mutateAsync: createOrUpdateDischarge} = useDischargeCRUD(
        !!data?.id ? QueryCRUDTypes.update : QueryCRUDTypes.create,
        user?.id,
    )

    const {mutateAsync: dischargePatient} = useDischargePatient(
        user?.id,
        currentViewedUser?.id,
    )

    const handleOnSubmit = useCallback(
        async value => {
            let toPostData = {
                ...value,
                userId: currentViewedUser?.id ? currentViewedUser?.id : null,
                id: !!data?.id ? data?.id : null,
                createdAt: !!value?.createdAt
                    ? new Date(value?.createdAt)
                    : null,
                updatedAt: !!value?.updatedAt
                    ? new Date(value?.updatedAt)
                    : null,
            }

            const dischargeData = await createOrUpdateDischarge(toPostData)
            if (dischargeData?.id) {
                //TODO: Temporary Refetch Table
                queryClient.invalidateQueries({
                    queryKey: dischargeQueryKeys,
                })

                disable.value = false
            }
            // mutate(toPostData)
        },
        [
            currentViewedUser?.id,
            data?.id,
            createOrUpdateDischarge,
            queryClient,
            dischargeQueryKeys,
            disable,
        ],
    )

    const handleOnDelete = useCallback(
        async (deleted, data) => {
            let toPostData = {
                id: currentViewedUser?.id,
                deleted: deleted,
            }

            if (!data?.id) {
                showNotification(
                    severityNotification.warn,
                    'You must save discharge information first !!',
                )
                return
            }

            const userData = await dischargePatient(toPostData)

            if (!!userData?.id) {
                handleChangeCurrentViewedUser(userData)

                // queryClient.invalidateQueries({
                //     queryKey: dischargeQueryKeys,
                // })

                // queryClient.invalidateQueries({
                //     queryKey: patientQueryKeys,
                // })

                visible.value = false
            }
        },
        [
            currentViewedUser?.id,
            dischargePatient,
            handleChangeCurrentViewedUser,
            visible,
        ],
    )
    //#endregion

    //#region Memo
    const dataList = useMemo(() => {
        return getComposedDataList(dischargeList, data)
    }, [data])
    //#endregion

    //#region Render
    const renderItem = useCallback(
        itemList => {
            return (
                itemList &&
                itemList?.length > 0 &&
                itemList?.map((item, idx) => (
                    <div
                        id={item.key}
                        className={`${
                            item.className ? item.className : 'col-12 md:col-6'
                        } `}
                        key={`item-${item.key}`}>
                        {!item.hideHeader &&
                            item.component !== component.checkbox && (
                                <h5 className={customLabelClassName}>
                                    {translateKeys(convertKeyToLabel(item.key))}

                                    {item.shorten && (
                                        <span> ( {item.shorten} )</span>
                                    )}
                                </h5>
                            )}

                        {/* RENDER COMPONENTS */}
                        {item.component === component.inputText ||
                        item.component === component.inputNumber ||
                        item.component === component.password ? (
                            <div className="">
                                <InputText
                                    id={item.key}
                                    className={`${customFontSize} custom-detail h-3rem`}
                                    placeholder={translateCommons(
                                        item.placeholder,
                                    )}
                                    keyfilter={
                                        item.keyFilter
                                            ? /^[^<>*!/^|?,+= ]+$/
                                            : ''
                                    }
                                    value={(!!data && data[item.key]) || ''}
                                    onChange={e =>
                                        queryClient.setQueryData(
                                            dischargeQueryKeys,
                                            () => {
                                                return {
                                                    ...data,
                                                    [item.key]: e.target.value,
                                                }
                                            },
                                        )
                                    }
                                    type={
                                        item.component === component.inputNumber
                                            ? 'number'
                                            : item.component ===
                                              component.password
                                            ? 'password'
                                            : 'text'
                                    }
                                />
                                <span className="bar"></span>
                            </div>
                        ) : item.component === component.inputTextArea ? (
                            <div className="">
                                <InputTextarea
                                    id={item.key}
                                    className={`${customFontSize} custom-detail`}
                                    placeholder={translateCommons(
                                        item.placeholder,
                                    )}
                                    value={(!!data && data[item.key]) || ''}
                                    rows={5}
                                    cols={30}
                                    autoResize
                                    onChange={e => {
                                        queryClient.setQueryData(
                                            dischargeQueryKeys,
                                            () => {
                                                return {
                                                    ...data,
                                                    [item.key]: e.target.value,
                                                }
                                            },
                                        )
                                    }}
                                />
                                <span
                                    className="bar "
                                    style={{bottom: '1px'}}></span>
                            </div>
                        ) : null}

                        {item?.emptyMessage && (
                            <div className="col-12 px-0" style={{color: 'red'}}>
                                {item.emptyMessage}
                            </div>
                        )}
                    </div>
                ))
            )
        },
        [
            data,
            dischargeQueryKeys,
            queryClient,
            translateCommons,
            translateKeys,
        ],
    )

    const renderGroupItem = useCallback(
        dataList => {
            return (
                dataList &&
                dataList?.length > 0 &&
                dataList?.map(item => {
                    const parentLabel = convertKeyToLabel(item.key)

                    return (
                        <div
                            key={`group-${item.key}`}
                            className={'grid col-12 p-0'}
                            style={{borderBottom: '1px dashed lightgray'}}>
                            <div
                                className="col-12 px-2 relative"
                                style={{position: 'relative'}}>
                                <div style={!parentLabel ? {top: 0} : {}}>
                                    {!!parentLabel && (
                                        <div className="flex mb-1 border-round justify-content-center align-items-center py-2 px-2 flex-wrap bg-teal-50 ">
                                            <h5
                                                className={`pl-2 text-darkcyan font-bold w-full m-0 py-2 flex align-items-center text-center md:text-left`}>
                                                <span className="mr-3">
                                                    <FaMinusCircle
                                                        style={{
                                                            width: '18px',
                                                            height: '28px',
                                                        }}
                                                    />
                                                </span>
                                                {translateCommons(parentLabel)}
                                            </h5>
                                        </div>
                                    )}
                                    {item?.description &&
                                    typeof item?.description === 'string' ? (
                                        <p>
                                            <div>
                                                <p className="mb-2">
                                                    {translateCommons(
                                                        'Fill in the following information to discharge the patient',
                                                    )}
                                                </p>
                                                <p>
                                                    <i>
                                                        {translateCommons(
                                                            'Note: Discharged patients will not be shown in the patient list. However, you can filter to see discharged patients',
                                                        )}
                                                    </i>
                                                </p>
                                            </div>
                                        </p>
                                    ) : (
                                        <div>
                                            <div>
                                                <p className="mb-2">
                                                    {translateCommons(
                                                        'Fill in the following information to discharge the patient',
                                                    )}
                                                </p>
                                                <p>
                                                    <i>
                                                        {translateCommons(
                                                            'Note: Discharged patients will not be shown in the patient list. However, you can filter to see discharged patients',
                                                        )}
                                                    </i>
                                                </p>
                                            </div>
                                        </div>
                                    )}{' '}
                                    {!!currentViewedUser?.deleted && (
                                        <Button
                                            className="col-12 p-3 mt-3"
                                            label={translateCommons(
                                                'PATIENT DISCHARGE RECORD',
                                            )}
                                            icon="pi pi-file"
                                            onClick={() =>
                                                exportRef.current.showForm()
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                            {item.children && (
                                <div className={'col-12 grid '}>
                                    {renderItem(item.children)}
                                </div>
                            )}
                        </div>
                    )
                })
            )
        },
        [
            translateKeys,
            currentViewedUser?.deleted,
            translateCommons,
            renderItem,
        ],
    )
    //#endregion

    return (
        <div>
            <ExportDischargePdf
                ref={exportRef}
                userData={currentViewedUser}
                dischargeData={data}
            />
            <div className="flex align-items-center flex-wrap col-12 px-2">
                <div className="flex mb-2 border-round justify-content-center align-items-center py-2 px-2 flex-wrap bg-teal-50 w-full">
                    <h5
                        className={`pl-2 text-darkcyan font-bold w-full m-0 py-2 text-center md:text-left`}>
                        <span className="mr-3">
                            <i className="pi pi-history font-bold text-xl"></i>
                        </span>
                        {translateCommons("Discharge Information's Audit Log")}
                    </h5>
                </div>
                <div
                    className={`col-12 m-0 mb-1 p-0 flex align-items-center justify-content-between`}>
                    <i className="underline m-0 text-700 text-lg">
                        {translateCommons(
                            "The audit log shows various changes in patient's discharge information, and you can view the entire change history.",
                        )}
                    </i>
                    <AuditLogButton
                        label={translateCommons("Show Discharge's Audit Log")}
                        onClick={() => {
                            if (!!currentViewedUser?.id && !!data?.id) {
                                genericRef.current.showForm({
                                    refId: data?.id,
                                    type: 'dischargeLog',
                                })
                            } else {
                                showNotification(
                                    severityNotification.warn,
                                    'No data found !!',
                                )
                            }
                        }}
                    />
                </div>
            </div>
            <hr />
            <div className="col-12 p-0">{renderGroupItem(dataList)}</div>
            {currentViewedUser?.accountStatus !== 'discharged' && (
                <div
                    className={` ${
                        !handleCheckPermissionByKey(
                            permissionOptionKeys?.allowToDischargePatient,
                        )
                            ? 'hidden'
                            : ''
                    } grid col-12 p-0`}>
                    <div className="col-6">
                        {currentViewedUser?.accountStatus ===
                        'awaiting_discharge' ? (
                            <Button
                                className="col-12 p-3 p-button-danger text-center"
                                label={translateCommons(`CANCEL DISCHARGE`)}
                                icon="pi pi-replay"
                                id="discharge-btn"
                                onClick={() => {
                                    handleOnDelete(false, data)
                                }}
                            />
                        ) : (
                            <Button
                                className=" col-12 p-3 p-button-info"
                                label={translateCommons('COMPLETE DISCHARGE')}
                                icon="pi pi-times"
                                disabled={data?.id ? false : disable.value}
                                onClick={() => (visible.value = true)}
                            />
                        )}
                    </div>
                    <div className="col-6">
                        <Button
                            className="col-12 p-3 "
                            label={translateCommons('SAVE')}
                            icon="pi pi-save"
                            onClick={() => handleOnSubmit(data)}
                        />
                    </div>
                </div>
            )}
            <Dialog
                header={translateCommons('Confirm Discharge')}
                visible={visible.value}
                style={{width: '40vw'}}
                breakpoints={{'960px': '75vw', '641px': '100vw'}}
                onHide={() => (visible.value = false)}>
                <div className="col-12">
                    <div className=" col-12 text-center mb-5">
                        <Image
                            src={dischargeImg}
                            alt="Avatar"
                            width="120px"
                            className="border-circle"
                        />
                        <p>
                            {translateCommons(
                                "Note: Patient will be permanently discharged after 48 hours. So you can only restore the patient's information within 48 hours before completely being discharged.",
                            )}
                        </p>
                    </div>
                    <div className="flex col-12">
                        <Button
                            className="col-6 p-3"
                            label={translateCommons('Yes, I understand')}
                            icon="pi pi-check"
                            onClick={() => handleOnDelete(true, data)}
                        />
                        <Button
                            label={translateCommons('No, not right now')}
                            icon="pi pi-times"
                            onClick={() => (visible.value = false)}
                            className=" ml-2 p-button-text col-6 p-3"
                        />
                    </div>
                </div>
            </Dialog>
            <GenericLogTable ref={genericRef} />
        </div>
    )
}

export default Discharge
